import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import { useEnterpriseContext } from '../contexts/EnterpriseContext';
import BackButton from '../components/BackButton';
import { navigateWithBack } from '../utils/navigation';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, Star, Clock, ChevronLeft, ChevronRight, ArrowRight, Briefcase, Building2 } from 'lucide-react';

const API = getApiUrl();

const SORT_OPTIONS = [
  { value: 'rating', label: 'Mieux notés' },
  { value: 'price_asc', label: 'Prix croissant' },
  { value: 'price_desc', label: 'Prix décroissant' },
  { value: 'sessions', label: 'Plus expérimentés' },
];

const SORT_OPTIONS_B2B = [
  { value: 'rating', label: 'Mieux notés' },
  { value: 'sessions', label: 'Plus expérimentés' },
];

function CoachCard({ coach, onClick, index, b2bMode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.06, ease: 'easeOut' }}
      data-testid={`coach-card-${coach.user_id}`}
      onClick={onClick}
      className="group lt-card cursor-pointer overflow-hidden"
    >
      {/* Photo */}
      <div className="aspect-[4/3] relative overflow-hidden bg-[#F0EDE6]">
        {coach.profile_photo_url ? (
          <img src={coach.profile_photo_url} alt={coach.display_name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#F5F0E3] to-[#E8DFC8]">
            <div className="lt-avatar w-24 h-24 rounded-2xl flex items-center justify-center">
              <span className="text-3xl font-display">{coach.display_name?.charAt(0)?.toUpperCase()}</span>
            </div>
          </div>
        )}
        {/* Rating badge */}
        {coach.rating_avg > 0 && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm shadow-sm border border-[#E8E5DE]">
            <Star className="w-3 h-3 lt-star" />
            <span className="text-[#111827] text-xs font-semibold">{coach.rating_avg?.toFixed(1)}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="text-lg font-display font-medium lt-title mb-1.5 group-hover:text-[#92710A] transition-colors">
          {coach.display_name}
        </h3>
        <p className="font-body lt-subtitle text-sm line-clamp-2 mb-4 leading-relaxed">{coach.bio}</p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {coach.specialties?.slice(0, 3).map((s, i) => {
            const name = typeof s === 'string' ? s : s.name;
            return <span key={i} className="lt-tag font-body">{name}</span>;
          })}
          {coach.specialties?.length > 3 && (
            <span className="lt-tag font-body" style={{ background: '#F0EDE6', color: '#6B7280', border: '1px solid #E0DCD3' }}>
              +{coach.specialties.length - 3}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-[#E8E5DE]">
          <div className="flex items-center gap-3 lt-muted text-xs font-body">
            <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{coach.session_duration || 60} min</span>
            <span>{coach.total_sessions || 0} sessions</span>
          </div>
          {b2bMode ? (
            <div className="flex items-center gap-1" data-testid={`coach-card-b2b-badge-${coach.user_id}`}>
              <span className="text-[10px] uppercase tracking-wider text-[#047857] font-semibold bg-[#D1FAE5] border border-[#A7F3D0] px-2 py-1 rounded-full inline-flex items-center gap-1">
                <Briefcase className="w-3 h-3" /> Inclus
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-[#92710A] text-lg font-display font-semibold">{coach.hourly_rate}€</span>
              <span className="lt-muted text-xs">/h</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function Marketplace() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const { isCollaborator, totalSessionsRemaining, nextExpiryAt, orders } = useEnterpriseContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [coaches, setCoaches] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'rating');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showFilters, setShowFilters] = useState(false);

  const fetchCoaches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedSpecialty) params.set('specialty', selectedSpecialty);
      params.set('sort_by', sortBy);
      params.set('page', page.toString());
      params.set('limit', '12');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API}/marketplace/coaches?${params}`, { headers });
      const data = await res.json();
      setCoaches(data.coaches || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, selectedSpecialty, sortBy, page, token]);

  const fetchSpecialties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/marketplace/specialties`);
      const data = await res.json();
      setSpecialties(data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchCoaches(); }, [fetchCoaches]);
  useEffect(() => { fetchSpecialties(); }, [fetchSpecialties]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedSpecialty) params.set('specialty', selectedSpecialty);
    if (sortBy !== 'rating') params.set('sort', sortBy);
    if (page > 1) params.set('page', page.toString());
    setSearchParams(params, { replace: true });
  }, [search, selectedSpecialty, sortBy, page, setSearchParams]);

  return (
    <div className="theme-light min-h-screen">
      {/* 🔒 Iter252 — Bandeau B2B retiré (doublon avec Mon Espace). Le CTA "Mon espace"
          subsiste dans le hero, et les informations détaillées (sessions, validité,
          commandes actives) sont consolidées sur la page Mon Espace. */}

      {/* Hero */}
      <div className="lt-hero pt-20 sm:pt-24 pb-12 border-b border-[#E8E5DE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <BackButton to="/training" label="Retour au tableau de bord" />
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <p className="uppercase text-xs tracking-[0.25em] lt-accent mb-4 font-body font-medium"></p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-medium lt-title tracking-tighter mb-4">
              Trouvez votre coach
            </h1>
            <p className="font-body lt-subtitle text-base sm:text-lg max-w-2xl leading-relaxed">
              Connectez-vous avec des coachs certifiés pour un accompagnement personnalisé et transformateur.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }}
            className="flex flex-wrap gap-3 mt-6">
            <button data-testid="become-coach-cta"
              onClick={() => {
                if (user) {
                  fetch(`${API}/coach/profile`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json())
                    .then(d => navigate(d.has_profile ? '/coach/dashboard' : '/devenir-coach'))
                    .catch(() => navigate('/devenir-coach'));
                } else { navigate('/devenir-coach'); }
              }}
              className="px-6 py-3 lt-btn-gold font-display text-sm inline-flex items-center gap-2 shadow-sm">
              Espace Coach <ArrowRight className="w-4 h-4" />
            </button>
            {user && (
              <button data-testid="marketplace-my-sessions-cta"
                onClick={() => navigateWithBack(navigate, location, '/my-bookings')}
                className="px-6 py-3 bg-white border-2 border-[#D4AF37] text-[#92710A] rounded-full font-display text-sm inline-flex items-center gap-2 hover:bg-[#FEF9E7] transition-all shadow-sm">
                Mes sessions <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {isCollaborator && (
              <button data-testid="marketplace-my-company-space-cta"
                onClick={() => navigateWithBack(navigate, location, '/my-company-space')}
                className="px-6 py-3 bg-gradient-to-r from-[#047857] to-[#065F46] text-white rounded-full font-display text-sm inline-flex items-center gap-2 hover:from-[#065F46] hover:to-[#064E3B] transition-all shadow-sm">
                <Building2 className="w-4 h-4" /> Mon espace <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-28 sm:pb-8">
        {/* Search & Filters */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 lt-search flex items-center">
            <Search className="absolute left-4 w-4 h-4 lt-muted" />
            <input data-testid="marketplace-search" type="text" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Rechercher par nom ou spécialité..."
              className="w-full pl-11 pr-4 py-3 bg-transparent border-0 outline-none lt-title text-sm font-body placeholder:text-[#6B7280]" />
          </div>
          <button data-testid="marketplace-filter-btn" onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-5 py-2.5 lt-surface lt-subtitle text-sm hover:lt-title transition-all font-body cursor-pointer">
            <SlidersHorizontal className="w-4 h-4" /> Filtres
          </button>
          <select data-testid="marketplace-sort" value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="px-4 py-2.5 bg-white border border-[#DDD9D0] rounded-xl lt-subtitle text-sm font-body cursor-pointer">
            {(isCollaborator ? SORT_OPTIONS_B2B : SORT_OPTIONS).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </motion.div>

        {/* Specialty filters */}
        {showFilters && specialties.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="flex flex-wrap gap-2 mb-8">
            <button onClick={() => { setSelectedSpecialty(''); setPage(1); }}
              className={`px-4 py-1.5 rounded-full text-xs font-body transition-all ${!selectedSpecialty ? 'bg-[#111827] text-white font-medium' : 'bg-white border border-[#DDD9D0] lt-subtitle hover:border-[#D4AF37]'}`}>
              Toutes
            </button>
            {specialties.map(s => {
              const name = typeof s === 'string' ? s : s.name;
              return (
                <button key={name} onClick={() => { setSelectedSpecialty(name); setPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-body transition-all ${selectedSpecialty === name ? 'bg-[#111827] text-white font-medium' : 'bg-white border border-[#DDD9D0] lt-subtitle hover:border-[#D4AF37]'}`}>
                  {name}
                </button>
              );
            })}
          </motion.div>
        )}

        <p className="lt-muted text-sm mb-6 font-body">{total} coach{total > 1 ? 's' : ''} disponible{total > 1 ? 's' : ''}</p>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="lt-card overflow-hidden">
                <Skeleton className="aspect-[4/3] bg-[#F0EDE6]" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-5 w-2/3 bg-[#F0EDE6] rounded" />
                  <Skeleton className="h-4 w-full bg-[#F0EDE6] rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : coaches.length === 0 ? (
          <div className="text-center py-20" data-testid="marketplace-empty">
            <p className="lt-muted font-body">
              {isCollaborator
                ? "Aucun coach n'est encore disponible pour votre forfait entreprise."
                : 'Aucun coach ne correspond à vos critères.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaches.map((coach, i) => (
              <CoachCard key={coach.user_id} coach={coach} index={i} b2bMode={isCollaborator}
                onClick={() => navigate(`/marketplace/coach/${coach.user_id}`)} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              className="p-2.5 lt-surface rounded-xl disabled:opacity-30">
              <ChevronLeft className="w-4 h-4 lt-title" />
            </button>
            <span className="lt-subtitle text-sm font-body">Page {page} / {totalPages}</span>
            <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
              className="p-2.5 lt-surface rounded-xl disabled:opacity-30">
              <ChevronRight className="w-4 h-4 lt-title" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
